import {
  Action,
  ActionIcon,
  Card, Form, FormHandlers,
  ICON_TRASH_CAN_OUTLINE,
  Label,
  Stack,
  TextArea,
} from '@doveit/bricks';
import React from 'react';
import { formatEuro } from '@doveit/hammer';
import { uniqueId } from 'lodash';
import { BooleanString, booleanStringToBool } from '../../../../utils/boolean-string/booleanString';
import { validationSchema } from './CounterOfferForm.schema';

export interface CounterOfferFormModel {
  isValueChanged: BooleanString,
  value: string,
  contractAmendments: {
    key: string,
    value: string
  }[],
}

export interface CounterOfferFormProps {
  initialValues: Partial<CounterOfferFormModel>,
  offerValue: number,
  onSubmit: (values: CounterOfferFormModel) => void,
  loading?: boolean,
  disabled?: boolean,
  innerRef?: React.MutableRefObject<FormHandlers>,
}

export const DEFAULT_INITIAL_VALUES: CounterOfferFormModel = {
  isValueChanged: '',
  value: '',
  contractAmendments: [],
};

const CounterOfferForm = ({
  initialValues,
  offerValue,
  onSubmit,
  loading,
  disabled,
  innerRef,
}: CounterOfferFormProps) => {
  const mergedValues = React.useMemo(() => ({
    ...DEFAULT_INITIAL_VALUES,
    ...initialValues,
  }), [initialValues]);
  const [currentAmendment, setCurrentAmendment] = React.useState<string>('');

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={mergedValues}
      validationSchema={validationSchema(offerValue)}
      disabled={disabled}
      loading={loading}
      innerRef={innerRef}
    >
      {({ values, setFieldValue }) => (
        <>
          <Form.Fieldset legend="Prezzo">
            <Form.Item size={{ MD: 1 / 2 }}>
              <Form.YesOrNotButtons
                name="isValueChanged"
                label="Vuoi proporre un prezzo diverso?"
                aria-label="Campo per specificare una modifica di prezzo della controproposta"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (event.target.value === 'false') {
                    setFieldValue('value', mergedValues.value);
                  }
                }}
                required
              />
            </Form.Item>
            <Form.Item size={{ MD: 1 / 2 }}>
              <Form.PriceField
                name="value"
                label="Nuovo prezzo"
                aria-label="Campo per inserire il nuovo prezzo della controproposta"
                disabled={!booleanStringToBool(values.isValueChanged)}
                help={`Proposta originale: ${formatEuro(offerValue)}`}
                required={booleanStringToBool(values.isValueChanged)}
              />
            </Form.Item>
          </Form.Fieldset>
          <Form.Fieldset legend="Modifiche contrattuali">
            <Form.Item>
              <Form.FieldArray
                name="contractAmendments"
                aria-label="Campo per inserire le modifiche contrattuali della controproposta"
                render={({ push, replace, remove }) => (
                  <Stack gap={400}>
                    <Stack gap={200}>
                      <Label text="Vuoi proporre un’altra modifica?" />
                      <Card>
                        <Card.Header
                          title={`Modifica #${values.contractAmendments.length + 1}`}
                          primaryActions={[
                            <Action
                              key="add-amendment-action"
                              size="S"
                              color="neutral"
                              emphasis="low"
                              label="Aggiungi"
                              aria-label="Pulsante per aggiungere una nuova modifica contrattuale alla controproposta"
                              disabled={currentAmendment.trim().length === 0}
                              onClick={() => {
                                push({ key: uniqueId('amendment'), value: currentAmendment.trim() });
                                setCurrentAmendment('');
                              }}
                            />,
                          ]}
                        />
                        <Card.Content>
                          <TextArea
                            label="Modifiche contrattuali"
                            value={currentAmendment}
                            aria-label="Campo per aggiungere una nuova modifica contrattuale alla controproposta"
                            onChange={(event) => setCurrentAmendment(event.target.value)}
                            placeholder="Inserisci qui il testo della modifica"
                          />
                        </Card.Content>
                      </Card>
                    </Stack>

                    {values.contractAmendments.length > 0 && (
                      <Stack gap={150}>
                        {values.contractAmendments.map((amendment, index) => (
                          <Card key={amendment.key}>
                            <Card.Header
                              title={`Modifica #${index + 1}`}
                              primaryActions={[
                                <ActionIcon
                                  key={`delete-amendment-${amendment.key}`}
                                  size="S"
                                  color="neutral"
                                  icon={{ path: ICON_TRASH_CAN_OUTLINE }}
                                  emphasis="low"
                                  label="Rimuovi modifica contrattuale"
                                  aria-label="Pulsante per rimuovere una modifica contrattuale alla controproposta"
                                  onClick={() => remove(index)}
                                />,
                              ]}
                            />
                            <Card.Content>
                              <Form.TextArea
                                name={`contractAmendments[${index}].value`}
                                label="Modifiche contrattuali"
                                aria-label="Campo per inserire le modifiche contrattuali della controproposta"
                                onBlur={() => replace(index, { ...amendment, value: values.contractAmendments[index].value.trim() })}
                              />
                            </Card.Content>
                          </Card>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                )}
              />
            </Form.Item>
          </Form.Fieldset>
        </>
      )}

    </Form>
  );
};

export default CounterOfferForm;
